import { CodeVerifier } from '@/stores/auth/accessors'
import { base64URLEncode, generateNonce, sha256, str2ab } from '@/stores/auth/functions'
import getEnv from '@/util/env'

const providerUrl = (): string => {
  const configured = getEnv('VITE_OIDC_PROVIDER_URL')
  if (!configured) {
    throw new Error('VITE_OIDC_PROVIDER_URL is not configured')
  }
  return configured.replace(/\/+$/, '')
}

const endpoint = (path: string): string =>
  `${providerUrl()}/protocol/openid-connect/${path}`

/**
 * Scopes requested at the authorization endpoint.
 *
 * `organization` is an *optional* client scope in Keycloak, so it is only granted
 * when asked for by name here — a default scope would arrive without being
 * listed. It carries the organization claim used to tell a user's own tenant from
 * the others.
 *
 * Add new scopes here rather than at the token endpoint: the authorization_code
 * grant has no scope parameter (RFC 6749 §4.1.3), so the scope is fixed by the
 * authorization request that issued the code.
 */
export const OIDC_SCOPES = 'openid organization'

export const generateTokenEndpoint = (): string => endpoint('token')

const generateCodeChallengeData = async () => {
  const state = await generateNonce()
  const codeVerifier = await generateNonce()
  const codeChallengeInput = await sha256(str2ab(codeVerifier))
  const code_challenge = base64URLEncode(codeChallengeInput)
  CodeVerifier.set(state, codeVerifier)
  return {
    state,
    code_challenge_method: 'S256',
    code_challenge,
    response_type: 'code',
    scope: OIDC_SCOPES
  }
}

/**
 * Keycloak required actions the portal launches as application-initiated actions. Each must
 * be enabled in the realm, or Keycloak refuses the request.
 *
 * `DELETE_CREDENTIAL` needs the id of the credential to remove, passed as `kcActionParameter`.
 */
export const KC_ACTIONS = {
  TOTP: 'CONFIGURE_TOTP',
  // A security key used after the password, not the passwordless registration.
  WEBAUTHN: 'webauthn-register',
  // Replaces any set already held, so regenerating invalidates the old codes.
  BACKUP_CODES: 'CONFIGURE_RECOVERY_AUTHN_CODES',
  DELETE_CREDENTIAL: 'delete_credential',
} as const

export type KcAction = (typeof KC_ACTIONS)[keyof typeof KC_ACTIONS]

/**
 * `kcAction` asks Keycloak for an application-initiated action: it runs that one action on
 * its own screens and returns here, which is how a credential is managed without sending the
 * user to the account console — that would also expose organization and group membership, the
 * applications list, and a password form. Keycloak reports the outcome on the way back as
 * `kc_action_status`.
 *
 * Enrollment cannot be done in the portal instead: no admin API creates a TOTP credential.
 */
export const generateLoginUrl = async (
  kcAction?: KcAction,
  kcActionParameter?: string,
): Promise<string> => {
  const codeParams = await generateCodeChallengeData()
  // Keycloak takes an action's parameter after a colon in the same query value.
  const kcActionValue =
    kcAction && kcActionParameter ? `${kcAction}:${kcActionParameter}` : kcAction
  const urlParams = {
    client_id: getEnv('VITE_OIDC_CLIENT_ID'),
    redirect_uri: `${window.location.origin}/redirect`,
    ...codeParams,
    ...(kcActionValue ? { kc_action: kcActionValue } : {}),
  }
  const urlParamsString = new URLSearchParams(urlParams).toString()
  return `${endpoint('auth')}?${urlParamsString}`
}

export const generateLogoutUrl = async (id_token: string | null): Promise<string> => {
  const urlParams = {
    client_id: getEnv('VITE_OIDC_CLIENT_ID'),
    post_logout_redirect_uri: `${window.location.origin}/redirect`,
    state: await generateNonce(),
    // Left out rather than sent empty: the provider treats the two the same, and
    // an empty hint reads as though one was meant to be there.
    ...(id_token ? { id_token_hint: id_token } : {}),
  }
  const urlParamsString = new URLSearchParams(urlParams).toString()
  return `${endpoint('logout')}?${urlParamsString}`
}
